import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const { t } = useTranslation();

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于开云体育',
    description: '开云体育品牌介绍、信誉展示与联系方式。开云体育是领先的综合体育平台，提供全球体育赛事资讯与专业服务。',
    url: 'https://www.kaiyunsports.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '开云体育',
      url: 'https://www.kaiyunsports.com',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 开云体育 | 品牌介绍与联系方式"
        description="了解开云体育的品牌故事、发展历程与核心价值。开云体育是领先的综合体育平台，汇聚全球顶级体育内容，提供赛事资讯、直播与专业数据分析服务。"
        keywords="开云体育,关于开云体育,开云体育品牌,开云体育联系方式,体育平台介绍"
        canonicalPath="/about"
        schema={aboutSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={[{ label: t('nav.about') }]} />

            <article className="bg-background-50 border border-background-200/60 rounded-lg p-6 md:p-10 mb-10">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                {t('about.title')}
              </h1>

              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className="ri-building-line"></i>
                  </span>
                  {t('about.intro')}
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    开云体育成立于2018年，总部位于中国上海，是一家专注于为全球体育爱好者提供高品质体育资讯与数据服务的综合性平台。自创立以来，开云体育始终秉承&ldquo;专业、全面、及时&rdquo;的服务理念，致力于打造中国领先的体育信息服务平台。
                  </p>
                  <p>
                    经过多年发展，开云体育已建立起覆盖足球、篮球、网球、电竞、乒乓球、羽毛球、F1赛车等全球主流体育项目的全方位内容体系。平台拥有一支由资深体育编辑、数据分析师和专业评论员组成的精英团队，全天候为用户提供最新、最全面的体育赛事资讯和专业分析。
                  </p>
                  <p>
                    我们的使命是让每一位体育爱好者都能在开云体育平台上找到自己热爱的内容，无论是实时赛事比分、深度战术分析，还是选手数据追踪、历史赛事回顾，开云体育都能满足您的需求。我们相信，体育的力量不仅在于竞技本身，更在于它能够连接人心、激发热情、传递正能量。
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center bg-accent-100 text-accent-700 rounded-lg">
                    <i className="ri-shield-check-line"></i>
                  </span>
                  {t('about.reputation')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mb-6">
                  <div className="bg-background-100 rounded-lg p-5 text-center">
                    <div className="text-3xl font-bold text-primary-600 mb-1 font-heading">500万+</div>
                    <p className="text-sm text-foreground-600">月活跃用户</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 text-center">
                    <div className="text-3xl font-bold text-accent-600 mb-1 font-heading">50+</div>
                    <p className="text-sm text-foreground-600">覆盖体育项目</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 text-center">
                    <div className="text-3xl font-bold text-secondary-600 mb-1 font-heading">99.9%</div>
                    <p className="text-sm text-foreground-600">数据准确率</p>
                  </div>
                </div>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    开云体育始终将用户体验和数据安全放在首位。平台采用先进的技术架构和严格的数据加密标准，确保用户信息的安全性和隐私保护。我们的赛事数据来源于官方授权渠道，经过多重校验后呈现给用户，确保数据的准确性和时效性。
                  </p>
                  <p>
                    凭借卓越的服务质量和良好的用户口碑，开云体育先后获得&ldquo;最佳体育资讯平台&rdquo;、&ldquo;用户最喜爱的体育APP&rdquo;等多项行业荣誉。这些荣誉不仅是对我们工作的肯定，更是激励我们不断前行的动力。
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-lg">
                    <i className="ri-eye-line"></i>
                  </span>
                  {t('about.vision')}
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    展望未来，开云体育将继续深耕体育资讯领域，持续丰富内容生态，拓展服务边界。我们计划在未来三年内，进一步扩大全球体育赛事的覆盖范围，引入人工智能和大数据技术优化内容推荐和赛事分析系统，为用户提供更加个性化、智能化的体育信息服务。
                  </p>
                  <p>
                    同时，开云体育也将积极履行企业社会责任，推动体育文化的传播与发展。我们希望通过平台的力量，让更多人了解体育、热爱体育、参与体育，共同感受体育带来的激情与快乐。
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className="ri-customer-service-2-line"></i>
                  </span>
                  {t('about.contact')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div className="bg-background-100 rounded-lg p-5 flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">contact@kaiyunsports.com</p>
                    </div>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-700 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                      <p className="text-sm text-foreground-600">400-888-6666</p>
                    </div>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-600">中国上海市浦东新区体育产业园区</p>
                    </div>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                      <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
              </section>
            </article>

            <div className="text-center pb-10">
              <p className="text-sm text-foreground-500 mb-4">感谢您对开云体育的关注与支持</p>
              <Link
                to="/category/football"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium px-6 py-2.5 rounded-md transition-colors text-sm whitespace-nowrap"
              >
                浏览赛事资讯
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}